import type { BoardStatus } from '@mm/shared';
import { useEffect, useRef } from 'react';
import type { TLStore } from 'tldraw';
import { markBoard, tickTime } from '../api/assignments';
import { getAccessToken } from '../api/client';

// События, которые считаем «активностью». passive=true — не блокируем скролл/pointer.
const ACTIVITY_EVENTS = ['pointermove', 'pointerdown', 'keydown', 'wheel'] as const;

// Пауза: если последняя активность была давнее 30 секунд — время не капает.
const IDLE_MS = 30_000;

// Флаш тиков: раз в 60 сек отправляем накопленное на сервер.
const FLUSH_MS = 60_000;

/**
 * Клиентский трекер для STUDENT на `/boards/:id`:
 * - одноразово метит доску `viewed` при монтировании;
 * - на первый user-event в tldraw переводит статус в `working`;
 * - считает активное время (сек, где за последние 30с было событие),
 *   флашит раз в 60с через `POST /api/boards/:id/time`;
 * - на unmount/beforeunload досылает накопленное (`keepalive: true`).
 */
export function useActiveTime(params: {
  boardId: string | null;
  enabled: boolean;
  store: TLStore | null;
  status: BoardStatus | null;
  onMarked: () => void;
}): void {
  const { boardId, enabled, store, status, onMarked } = params;

  const accRef = useRef(0);
  const lastActiveRef = useRef(Date.now());
  const onMarkedRef = useRef(onMarked);
  onMarkedRef.current = onMarked;

  // Mark 'viewed' + heartbeat активного времени.
  useEffect(() => {
    if (!boardId || !enabled) return;

    markBoard(boardId, 'viewed')
      .then(() => onMarkedRef.current())
      .catch(() => {
        // 204 для недопустимых переходов — ок, ошибка сети — тоже молча.
      });

    function touch() {
      lastActiveRef.current = Date.now();
    }
    ACTIVITY_EVENTS.forEach((e) =>
      document.addEventListener(e, touch, { passive: true } as AddEventListenerOptions),
    );

    const tickId = window.setInterval(() => {
      if (Date.now() - lastActiveRef.current < IDLE_MS) accRef.current += 1;
    }, 1000);

    async function flush() {
      const delta = accRef.current;
      if (delta < 1 || !boardId) return;
      accRef.current = 0;
      try {
        await tickTime(boardId, Math.min(delta, 300));
      } catch {
        // best-effort
      }
    }

    const flushId = window.setInterval(() => {
      void flush();
    }, FLUSH_MS);

    function onBeforeUnload() {
      const delta = accRef.current;
      if (delta < 1 || !boardId) return;
      accRef.current = 0;
      const token = getAccessToken();
      // keepalive: true сохраняет запрос при выгрузке страницы и пропускает заголовки.
      // sendBeacon не подходит — нам нужен Bearer.
      try {
        fetch(`/api/boards/${boardId}/time`, {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          credentials: 'include',
          keepalive: true,
          body: JSON.stringify({ delta: Math.min(delta, 300) }),
        }).catch(() => {});
      } catch {
        // ignore
      }
    }
    window.addEventListener('beforeunload', onBeforeUnload);

    return () => {
      window.clearInterval(tickId);
      window.clearInterval(flushId);
      window.removeEventListener('beforeunload', onBeforeUnload);
      ACTIVITY_EVENTS.forEach((e) => document.removeEventListener(e, touch));
      // Финальный flush (без keepalive, но и unmount обычно не равен закрытию вкладки).
      void flush();
    };
  }, [boardId, enabled]);

  // Mark 'working' на первый user-event в tldraw store.
  useEffect(() => {
    if (!store || !boardId || !enabled) return;
    // API позволяет working только из NEW/VIEWED/REVIEWED. Если уже WORKING
    // или FINISHED — хук ничего не делает.
    if (status === 'WORKING' || status === 'FINISHED') return;

    let fired = false;
    const unsub = store.listen(
      ({ source }) => {
        if (source !== 'user' || fired) return;
        fired = true;
        markBoard(boardId, 'working')
          .then(() => onMarkedRef.current())
          .catch(() => {});
      },
      { source: 'user', scope: 'document' },
    );
    return unsub;
  }, [store, boardId, enabled, status]);
}
